package com.devsusana.portfolio.pages.posts

import androidx.compose.runtime.Composable
import com.devsusana.portfolio.components.blog.*
import com.varabyte.kobweb.core.Page

@Page("/posts/hybrid-pattern-mobile-ai")
@Composable
fun HybridPatternMobileAiPage() {
    ArticleScaffold(
        title = "The Hybrid Pattern in Mobile AI: Deterministic Fast-Path (<5ms) vs LLM",
        date = "2026-09-15",
        readingTime = "7 min read",
        tags = listOf("Clean Architecture", "Kotlin", "Performance", "Edge AI", "Jetpack Compose"),
        isSpanish = false
    ) {
        ArticleSectionHeader("1. The Mobile AI Anti-Pattern: \"LLM-Only\" Architecture")

        ArticleParagraph(
            "A widespread mistake when adding AI to mobile products is routing every user utterance directly to the Large Language Model. While this looks simple on paper, it produces critical flaws in production:"
        )

        BulletItem("Unacceptable Latency (6–12 seconds):", "Running on-chip neural inference consumes substantial clock cycles. For straightforward operations (e.g. checking a student balance or listing cancelled classes), waiting several seconds destroys usability.")
        BulletItem("Battery & Thermal Degradation:", "Continuous neural processing heats up device chips and drains battery life rapidly.")
        BulletItem("Arithmetic & Factual Hallucinations:", "Even fine-tuned Small Language Models (1B–2B parameters) struggle with precise date math, calendar reconciliation, and accounting calculations.")

        ArticleSectionHeader("2. The Solution: A Two-Tier Hybrid Architecture")

        ArticleParagraph(
            "In HomeTutorPro, our on-device assistant SUE (Smart User Entity) uses a Two-Tier Hybrid Pattern:"
        )

        CodeSnippet(
            code = """           ┌────────────────────────────────────────┐
           │          Incoming User Intent          │
           └───────────────────┬────────────────────┘
                               │
                ┌──────────────┴──────────────┐
                ▼                             ▼
    [TIER 1: FAST-PATH]            [TIER 2: GENERATIVE LLM]
    • Pure Kotlin / Room SQL       • Local RAG + MediaPipe
    • Structured balance & agenda  • Complex pedagogical queries
    • Sub-5ms execution time       • Open-ended synthesis
    • 0% GPU utilization           • GPU-accelerated inference
    • 100% precision (0 halluc.)   • 2048 token context window""",
            language = "Two-Tier Architecture"
        )

        ArticleSectionHeader("3. Tier 1: Deterministic Fast-Path in Kotlin")

        ArticleParagraph(
            "For operational queries, SueAgent bypasses the LLM entirely through optimized Kotlin algorithms:"
        )

        BulletItem("Natural Language Time Engine:", "Parses explicit times (17:00, 21:30) as well as natural spoken expressions (at five, quarter past, ten to, midnight).")
        BulletItem("Contextual Afternoon Inference:", "If a tutor requests an appointment without specifying AM/PM (\"schedule a class at 5\"), the engine infers afternoon hours (17:00), matching the operational reality of private extracurricular tutoring.")
        BulletItem("Strict Schedule Collision Prevention:", "When rescheduling or booking extra lessons, SaveScheduleExceptionUseCase validates overlaps across existing classes, existing exceptions, and travel buffers. If a conflict occurs, mutation is blocked, and the tutor is immediately informed.")

        CodeSnippet(
            code = """// Example: Deterministic collision evaluation in Domain Layer
class SaveScheduleExceptionUseCase @Inject constructor(
    private val repository: ScheduleExceptionRepository,
    private val studentRepository: StudentRepository
) : ISaveScheduleExceptionUseCase {

    override suspend operator fun invoke(
        professorId: String,
        studentId: String,
        exception: ScheduleException
    ): Result<Unit, DomainError> {
        val allSchedules = studentRepository.getAllSchedules(professorId).first()
        
        // Fast-path deterministic collision check in < 5 ms
        for (existing in allSchedules) {
            if (isTimeOverlap(exception.newStartTime, exception.newEndTime, existing.startTime, existing.endTime)) {
                val studentName = studentRepository.getStudentById(existing.studentId)?.name ?: "Student"
                return Result.Error(
                    DomainError.ConflictingStudent(
                        studentName = studentName,
                        time = "${'$'}{existing.startTime} - ${'$'}{existing.endTime}"
                    )
                )
            }
        }
        repository.saveException(professorId, studentId, exception)
        return Result.Success(Unit)
    }
}""",
            language = "Kotlin"
        )

        ArticleSectionHeader("4. Voice Safety: Two-Step Confirmation Cards")

        ArticleParagraph(
            "Destructive database modifications (cancelling a booked class, recording payments, deleting records) must never occur based on raw acoustic ambiguity."
        )

        BulletItem("1. Intent Transition:", "SueAgent detects the destructive intent and transitions SueUiState to a Prepare state.")
        BulletItem("2. Visual Card Display:", "An interactive Jetpack Compose card (SuePendingAction) displays the exact mutation parameters.")
        BulletItem("3. Explicit Acoustic Prompt:", "SUE speaks a direct confirmation prompt (\"Confirm cancelling Lucia's lesson on Friday? Say yes or no\").")
        BulletItem("4. Transactional Execution:", "Only upon explicit voice affirmation (\"Yes\" / \"Confirm\") or UI tap is the database mutation use case executed.")

        ArticleSectionHeader("5. Tier 2: Generative Inference with Local RAG")

        ArticleParagraph(
            "Only when queries require open synthesis, relational advice, or complex pedagogical analysis does execution fall back to Gemma via MediaPipe GenAI:"
        )

        BulletItem("Pedagogical Planning:", "\"How do you recommend organizing Carlos's physics review before his exam?\"")
        BulletItem("Relational Resource Overview:", "\"What study materials have I shared with each student this week and what open time slots do I have left?\"")

        ArticleParagraph(
            "By feeding Gemma structured data from Tier 1, the model functions purely as an articulate summarizer, completely eliminating factual fabrication."
        )

        ArticleSectionHeader("6. Key Architecture Conclusions")

        BulletItem("Reserve LLMs for Language, Not Logic:", "Use Kotlin, Room indexes, and domain use cases for accounting, scheduling, and validation.")
        BulletItem("Speed is Feature #1:", "90% of user queries in mobile productivity apps are transactional. Serving them in < 5 ms creates an instantly responsive, reliable user experience.")

        CalloutBox {
            ArticleParagraph(
                "💡 Architectural Takeaway: Don't use a 2-billion-parameter neural network to compute 15 + 20 or check if 17:00 overlaps with 17:30. A hybrid architecture delivers the best of both worlds: instant sub-5ms deterministic precision and rich natural language fluency."
            )
        }
    }
}
